import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Pencil, Trash2, Tag, Copy, Check, Palette } from "lucide-react";
import type { Prompt, PromptCategory } from "../api/PromptLibraryApi";
import { extractPlaceholders, fillPlaceholders, placeholderLabel, splitIntoSegments, colorForPlaceholder, isPredefinedPlaceholder, resolvePredefinedValues, PLACEHOLDER_DEFAULTS } from "./placeholders";
import { usePromptLibrary } from "../modal/PromptLibraryContext";
import ConfirmModal from "shared/ConfirmModal";

interface PromptCardProps {
  prompt: Prompt;
  categories: PromptCategory[];
  canEdit: boolean;
  onEdit: (p: Prompt) => void;
  onDelete: (id: number) => void;
}

export default function PromptCard({ prompt, categories, canEdit, onEdit, onDelete }: PromptCardProps) {
  const { colorScheme } = usePromptLibrary();
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const token of extractPlaceholders(prompt.body)) {
      if (PLACEHOLDER_DEFAULTS[token]) initial[token] = PLACEHOLDER_DEFAULTS[token];
    }
    return initial;
  });
  const [copied, setCopied] = useState(false);
  const [editingDefaults, setEditingDefaults] = useState<Set<string>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  function toggleEditingDefault(token: string) {
    setEditingDefaults((s) => {
      const next = new Set(s);
      if (next.has(token)) next.delete(token);
      else next.add(token);
      return next;
    });
  }

  const category = categories.find((c) => c.id === prompt.category_id);
  const allPlaceholders = useMemo(() => extractPlaceholders(prompt.body), [prompt.body]);
  const placeholders = useMemo(() => allPlaceholders.filter((t) => !isPredefinedPlaceholder(t)), [allPlaceholders]);
  const predefinedValues = useMemo(() => resolvePredefinedValues(allPlaceholders, { colorScheme }), [allPlaceholders, colorScheme]);
  const mergedValues = useMemo(() => ({ ...values, ...predefinedValues }), [values, predefinedValues]);
  const finalText = useMemo(
    () => fillPlaceholders(prompt.body, mergedValues, new Set(Object.keys(predefinedValues))),
    [prompt.body, mergedValues, predefinedValues]
  );
  const segments = useMemo(() => splitIntoSegments(prompt.body), [prompt.body]);
  const hasColorScheme = allPlaceholders.includes("color_scheme") || allPlaceholders.includes("color_scheme_inline");

  async function confirmDelete() {
    setDeleting(true);
    try {
      await onDelete(prompt.id);
    } finally {
      setDeleting(false);
      setConfirmingDelete(false);
    }
  }

  async function handleCopy() {
    await navigator.clipboard.writeText(finalText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="bg-surface/60 border border-border/60 rounded-xl p-4 hover:border-border transition-colors">
      {confirmingDelete && (
        <ConfirmModal
          title="Delete prompt"
          message={`Delete "${prompt.title}"?`}
          confirmLabel="Delete"
          variant="danger"
          confirming={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}
      {/* Header row */}
      <div className="flex items-start gap-3 cursor-pointer select-none" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground leading-snug font-inter">{prompt.title}</p>

          {/* Meta badges */}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {category && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/40 text-muted-foreground border-border/40 font-inter">
                {category.name}
              </span>
            )}
            {placeholders.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-indigo-500/10 text-indigo-400 border-indigo-500/20 font-mono">
                {placeholders.length} placeholder{placeholders.length !== 1 ? "s" : ""}
              </span>
            )}
            {hasColorScheme && (
              <span
                className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-purple-500/10 text-purple-400 border-purple-500/20 font-mono"
                title="Filled automatically from your profile colors (set in Settings). Skipped if none are set."
              >
                <Palette className="w-2.5 h-2.5" />
                color scheme (auto)
              </span>
            )}
            {(prompt.tags ?? []).map((t) => (
              <span key={t} className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-surface-muted text-muted-foreground border border-border font-mono">
                <Tag className="w-2.5 h-2.5" />
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {canEdit && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(prompt); }}
                className="p-1.5 text-muted-foreground hover:text-primary hover:bg-surface-muted rounded-lg transition-colors"
                title="Edit"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmingDelete(true); }}
                disabled={deleting}
                className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-surface-muted rounded-lg transition-colors disabled:opacity-50"
                title="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors"
            title={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-border/50 space-y-3">
          {placeholders.length > 0 && (
            <div className="grid gap-2 sm:grid-cols-2">
              {placeholders.map((token) => {
                const color = colorForPlaceholder(token, placeholders);
                const hasDefault = token in PLACEHOLDER_DEFAULTS;
                const isEditing = editingDefaults.has(token);
                return (
                  <div key={token}>
                    <div className="flex items-center justify-between mb-1">
                      <label className={`block text-[11px] font-medium font-inter ${color.text}`}>
                        {placeholderLabel(token)}
                      </label>
                      {hasDefault && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleEditingDefault(token); }}
                          className="text-[11px] text-primary hover:text-primary/80 font-inter font-medium"
                        >
                          {isEditing ? "Done" : "Edit"}
                        </button>
                      )}
                    </div>
                    {hasDefault && !isEditing ? (
                      <textarea
                        value={values[token] || "Not set"}
                        readOnly
                        disabled
                        onClick={(e) => e.stopPropagation()}
                        className="w-full h-[94px] bg-surface-muted/30 border border-dashed border-border/60 rounded-lg px-2.5 py-1.5 text-xs text-muted-foreground font-inter resize-none cursor-not-allowed opacity-70"
                      />
                    ) : (
                      <textarea
                        value={values[token] ?? ""}
                        onChange={(e) => setValues((v) => ({ ...v, [token]: e.target.value }))}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus={hasDefault && isEditing}
                        className={`w-full h-[94px] bg-surface-muted border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground placeholder-muted-foreground focus:outline-none font-inter resize-y ${color.focusBorder}`}
                        placeholder={`Fill in ${placeholderLabel(token).toLowerCase()}…`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <pre className="whitespace-pre-wrap break-words text-xs text-slate-300 bg-slate-950/60 border border-slate-800 rounded-lg p-3 font-mono max-h-64 overflow-y-auto">
            {segments.map((seg, i) => {
              if (seg.type === "text") return <span key={i}>{seg.text}</span>;
              if (isPredefinedPlaceholder(seg.token!)) {
                const resolved = predefinedValues[seg.token!];
                if (!resolved) return <span key={i} />;
                const lines = resolved.split("\n");
                return (
                  <span key={i}>
                    {lines.map((line, j) => (
                      <span key={j}>
                        <span className="bg-purple-500/20 text-purple-300 rounded px-1 font-semibold">
                          {line}
                        </span>
                        {j < lines.length - 1 && <br />}
                      </span>
                    ))}
                  </span>
                );
              }
              const color = colorForPlaceholder(seg.token!, placeholders);
              const filled = values[seg.token!]?.trim();
              return (
                <span key={i} className={`${color.bg} ${color.text} rounded px-1 font-semibold`}>
                  {filled || seg.text}
                </span>
              );
            })}
          </pre>

          <button
            onClick={(e) => { e.stopPropagation(); handleCopy(); }}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl font-inter transition-colors ${
              copied ? "bg-emerald-600 text-white" : "bg-primary hover:bg-primary/90 text-primary-foreground"
            }`}
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
      )}
    </div>
  );
}
